/** The loadout screen's rules: weights, caps and what requisition a loadout costs (02 §8, 04 §6). */

import type { Inventory, Loadout } from '../sim/types';
import type { PriceList } from './api';
import type { HazardRules } from './content/career';
import { CAPS, PACKABLE, PRICE, STANDARD_ISSUE, WEIGHT } from './content/economy';
import { gearWeight } from './gear';
import { carryBonus } from './mods';
import type { RangerRecord } from './types';

const POD_LETTERS = 'ABCD';

/** What the ranger is handed for nothing on this run (Hazard 2 withholds a magazine). */
export function standardIssue(rules: HazardRules): Inventory {
  return { ...STANDARD_ISSUE, magazines: rules.standardMagazines };
}

export function weightOf(items: Inventory): number {
  let units = 0;
  for (const key of PACKABLE) units += items[key] * WEIGHT[key];
  return units;
}

/** Price list for the loadout screen. `free` (Classic) zeroes every price. */
export function priceList(ranger: RangerRecord | null, free: boolean): PriceList {
  const price = { ...PRICE };
  if (free) for (const key of Object.keys(price) as Array<keyof typeof price>) price[key] = 0;
  return {
    price, weight: { ...WEIGHT }, carryCap: CAPS.carry + (ranger && !free ? carryBonus(ranger) : 0),
    podCap: CAPS.pod, maxPods: CAPS.pods,
  };
}

function checkCounts(items: Inventory, where: string): void {
  for (const key of PACKABLE) {
    const whole = Number.isInteger(items[key]) && items[key] >= 0;
    if (!whole) throw new Error(`${where}: item counts must be whole numbers.`);
  }
  if (!Number.isInteger(items.roundsInMag) || items.roundsInMag < 0 || items.roundsInMag > CAPS.roundsInMag) {
    throw new Error(`${where}: the loaded magazine holds 0-${CAPS.roundsInMag} rounds.`);
  }
}

export interface LoadoutLimits {
  carryCap: number;
  maxPods: number;
  /** Carried items up to these counts cost nothing. */
  standard: Inventory;
  /** Classic: everything is free within the caps. */
  free: boolean;
}

/**
 * Validates a loadout against the caps and returns its RP cost: standard issue is free,
 * everything carried beyond it and everything in a pod is bought (04 §6). Rare equipment
 * costs nothing but weighs on the ranger; whether the stock holds it is `takeGear`'s business.
 * Throws an Error the loadout screen can show.
 */
export function priceLoadout(loadout: Loadout, limits: LoadoutLimits): number {
  checkCounts(loadout.carried, 'Ranger');
  const carried = weightOf(loadout.carried) + gearWeight(loadout.gear);
  if (carried > limits.carryCap) {
    throw new Error(`The ranger is carrying ${carried} units - the limit is ${limits.carryCap}.`);
  }
  if (loadout.pods.length > limits.maxPods) {
    throw new Error(`Only ${limits.maxPods} supply pods can be dropped on this mission.`);
  }
  let cost = 0;
  for (const key of PACKABLE) cost += Math.max(0, loadout.carried[key] - limits.standard[key]) * PRICE[key];
  loadout.pods.forEach((pod, i) => {
    const name = `Pod ${POD_LETTERS[i] ?? i + 1}`;
    checkCounts(pod, name);
    const units = weightOf(pod);
    if (units > CAPS.pod) throw new Error(`${name} holds ${units} units - the limit is ${CAPS.pod}.`);
    for (const key of PACKABLE) cost += pod[key] * PRICE[key];
  });
  return limits.free ? 0 : cost;
}
